import { z } from 'zod';
// SEO / site-health audit — Mag turns a DETERMINISTIC evidence bundle (Lighthouse
// /PageSpeed category scores + failed audits, plus the brand's scraped on-page
// copy and positioning) into concrete, evidence-cited fixes. The evidence is
// gathered deterministically and handed to the agent; the agent may only reason
// over what it's given. When there's nothing material to fix, it returns
// 'all_clear' rather than inventing busywork — the same no-hallucination contract
// as the funnel diagnosis.
/** Which lever a finding pulls. performance = speed / Core Web Vitals;
 *  on_page = technical/mechanical (meta, alt, viewport, crawlable links);
 *  messaging = does the copy say what the brand does + use its keywords. */
export const seoFindingAreaSchema = z.enum(['performance', 'on_page', 'messaging']);
export const seoFindingSchema = z.object({
    area: seoFindingAreaSchema,
    severity: z.enum(['high', 'medium', 'low']),
    /** A fact lifted from the evidence — cite the number/audit ("mobile
     *  performance score 0.34", "no meta description on /", "title is 'Home'").
     *  Generous cap: Lighthouse audit titles + measured values run long when the
     *  agent quotes them verbatim. */
    observation: z.string().min(1).max(600),
    /** The concrete fix. Short, imperative, lowercase (Mag voice). */
    recommendation: z.string().min(1).max(500),
    /** Optional: the check that would confirm the fix landed. */
    whatToTest: z.string().min(1).max(400).nullish(),
});
export const seoAuditOutputSchema = z.discriminatedUnion('decision', [
    z.object({
        decision: z.literal('audit'),
        /** One-line read of the site's health (Mag voice). */
        summary: z.string().min(1).max(500),
        findings: z.array(seoFindingSchema).min(1).max(8),
    }),
    z.object({
        decision: z.literal('all_clear'),
        /** Why there's nothing material to fix right now (honest, Mag voice). */
        summary: z.string().min(1).max(400),
    }),
]);
export const seoActionStatusSchema = z.enum(['implemented', 'dismissed']);
/** Founder verdict on a seo_audit card — recorded so Mag won't nag about an
 *  audit already acted on, and can phrase continuity next week. */
export const setSeoActionSchema = z.object({
    status: seoActionStatusSchema,
    note: z.string().max(600).nullish(),
});
/** Founder verdict on ONE finding of an audit card.
 *
 *  No `note`: the card-level note stays the place to say "did the perf ones,
 *  skipped the copy". Once each finding carries its own verdict, a per-finding
 *  note has far less to explain, and adding one later is additive — guessing
 *  wrong now means a column nobody fills. See spec 08d4bf9c, residue RES-1.
 *
 *  Shared by both seo_audit and ai_visibility, whose verdict vocabulary is the
 *  same enum for the same reason their action tables are twins. */
export const setFindingVerdictSchema = z.object({
    status: seoActionStatusSchema,
});
/** Lighthouse category scores, 0..1, mobile strategy. Null when PageSpeed was
 *  unreachable (the audit then rests on the on-page evidence only). */
export const seoLighthouseScoresSchema = z.object({
    performance: z.number().min(0).max(1).nullable(),
    seo: z.number().min(0).max(1).nullable(),
    accessibility: z.number().min(0).max(1).nullable(),
    bestPractices: z.number().min(0).max(1).nullable(),
});
/** Every probe reports one of three states, and the third is the point.
 *
 *  'ok' and 'absent' are findings. 'unchecked' is NOT: it means the probe
 *  could not look — a timeout, a refused host, a budget overrun. The agent's
 *  HARD RULE 1 forbids inventing facts, and a null that read as "missing"
 *  would have Mag telling a founder their robots.txt is gone when the truth
 *  is that we never reached it. A confident, wrong, founder-visible claim
 *  about their own site is worse than silence, so the uncertainty is carried
 *  in the type rather than flattened into one. */
export const probeStateSchema = z.enum(['ok', 'absent', 'unchecked']);
export const seoInfraEvidenceSchema = z.object({
    /** The redirect chain from the audited URL to whatever finally answers.
     *  Lighthouse's `redirects` audit reports an estimated time saving; this
     *  reports the chain, which is the only part that says WHICH hop to drop. */
    redirects: z.object({
        state: probeStateSchema,
        /** Number of hops before a non-redirect response. 0 means it served. */
        hops: z.number().int().min(0).nullish(),
        finalUrl: z.string().nullish(),
        /** Each hop as "308 https://..." so the agent can cite one precisely. */
        chain: z.array(z.string()).default([]),
    }),
    robots: z.object({
        state: probeStateSchema,
        status: z.number().int().nullish(),
        /** Whether robots.txt names a sitemap. Absent is not an error, but it is
         *  the cheapest thing a founder can add. */
        declaresSitemap: z.boolean().nullish(),
        /** True when a group disallows the whole site. Catastrophic and silent —
         *  worth its own finding, and nothing else in the pipeline can see it. */
        disallowsAll: z.boolean().nullish(),
    }),
    sitemap: z.object({
        state: probeStateSchema,
        status: z.number().int().nullish(),
        /** Where it was actually looked for — robots' declared URL if there was
         *  one, otherwise /sitemap.xml. The agent should cite the URL it means. */
        url: z.string().nullish(),
        /** An index lists child sitemaps, not pages. Without this distinction an
         *  index of 40 children reads as a sitemap with 40 URLs, or worse, as
         *  nearly empty. */
        kind: z.enum(['urlset', 'index']).nullish(),
        entryCount: z.number().int().min(0).nullish(),
        /** True when the read hit its byte budget, so entryCount is a floor
         *  rather than a total. Stops the agent asserting an exact number it
         *  cannot know. */
        countedAtLeast: z.boolean().nullish(),
    }),
    canonical: z.object({
        state: probeStateSchema,
        /** The href as declared on the page. */
        href: z.string().nullish(),
        /** Whether that href itself redirects. A canonical pointing at a redirect
         *  is discounted by Google and splits ranking signals — and Lighthouse
         *  passes it, so this field is the only place it can surface. */
        redirects: z.boolean().nullish(),
        resolvesTo: z.string().nullish(),
        /** Whether the canonical names a different host than the page serving it. */
        crossHost: z.boolean().nullish(),
    }),
});
/** The DETERMINISTIC evidence Mag reasoned over — the raw facts behind the
 *  recommendations. Surfaced in the detail view so the founder can see exactly
 *  what the analysis rests on (not just the conclusions). */
export const seoAuditEvidenceSchema = z.object({
    /** Failed Lighthouse audits, worst first (id, human title, measured value). */
    failedAudits: z
        .array(z.object({
        id: z.string(),
        title: z.string(),
        displayValue: z.string().nullish(),
    }))
        .default([]),
    /** The scraped on-page copy that was read. null = no scrape on file. */
    onPage: z
        .object({
        title: z.string(),
        description: z.string(),
        wordCount: z.number().int().min(0),
    })
        .nullable(),
    /** What the site's SEO infrastructure actually looks like — the facts
     *  Lighthouse structurally cannot supply (spec e8c169b1).
     *
     *  Measured, not assumed: a keyed PageSpeed run against a site whose
     *  robots.txt 404'd, whose sitemap.xml 404'd, and whose canonical pointed at
     *  a redirecting domain scored the SEO category 1.0 — a perfect 100/100 —
     *  and forwarded none of its eleven audits (theory d48545fa). Lighthouse has
     *  no sitemap audit at all, reports a missing robots.txt as notApplicable,
     *  and passes a cross-domain canonical. So these four probes are the only
     *  way the agent can ever see this class of problem.
     *
     *  Null when the gatherer did not run at all (an older card, or a brand with
     *  no resolvable URL). That is distinct from a probe reporting 'unchecked'. */
    infra: seoInfraEvidenceSchema.nullish(),
    /** The brand positioning the copy was judged against. */
    positioning: z.object({
        category: z.string(),
        positioning: z.string(),
        valueProps: z.array(z.string()),
    }),
});
/** API-facing result: the audited site + scores + the agent's output. */
export const brandSeoAuditSchema = z.object({
    url: z.string().url(),
    strategy: z.literal('mobile'),
    scores: seoLighthouseScoresSchema,
    output: seoAuditOutputSchema,
    /** The raw evidence the analysis rests on. */
    evidence: seoAuditEvidenceSchema,
    /** LLM cost of the audit (already metered to the org via clientId). */
    costUsd: z.number().min(0),
});
//# sourceMappingURL=mag-seo-audit.js.map